import { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    SttDialog,
    SttFormCheck,
    SttFormText,
    SttFormTextarea,
} from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import { PERMISSIONS } from '../../../lib/permissions';

type FileItem = {
    id?: string | number;
    name: string;
    description?: string;
    public?: boolean;
};

type Props = {
    item: FileItem;
    onSave: (file: FileItem) => void;
    onDelete: (file: FileItem) => void;
    onDownload: (file: FileItem) => void;
    onClose: () => void;
};

export default function ModalFile({
    item,
    onSave,
    onDelete,
    onDownload,
    onClose,
}: Props): React.ReactElement | null {
    const { t } = useTranslation();
    const [file, _setFile, setProperty] = useCrud<FileItem>(item);
    const [strippedName, setStrippedName] = useState<string>();
    const [extension, setExtension] = useState<string>();

    // On component load, separate file name and extension
    useEffect(() => {
        const parts = item.name.split('.');
        setExtension(parts[parts.length - 1]);
        setStrippedName(
            parts.reduce(
                (res, part, index) =>
                    res +
                    (index > 0 && index < parts.length - 1 ? `.${part}` : ''),
                parts[0],
            ),
        );
    }, []);

    // Idiot-proof file extension handling
    function setFileName(value: string): void {
        setStrippedName(value);
        setProperty({ name: 'name', value: `${value}.${extension}` });
    }

    return (
        <SttDialog
            maxWidth="sm"
            onClose={onClose}
            open
            title={t('file.file')}
            subtitle={file.name}
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 9 }}>
                        <SttFormText
                            caption={t('labels.name')}
                            defVal={strippedName}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setFileName(value as string);
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                        <SttFormText
                            disabled
                            caption={t('file.extension')}
                            defVal={extension}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SttFormTextarea
                            caption={t('labels.description')}
                            name="description"
                            defVal={file.description}
                            onChange={setProperty}
                        />
                    </Grid>
                    <Grid
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                        size={{ xs: 12 }}
                    >
                        <SttFormCheck
                            caption={t('file.docPublic')}
                            name="public"
                            checked={file.public}
                            onChange={setProperty}
                        />
                    </Grid>
                </Grid>
            }
            buttons={[
                {
                    type: 'download',
                    onClick: () => onDownload(file),
                },
                {
                    type: 'delete',
                    onClick: () => onDelete(file),
                    permission: PERMISSIONS.EVENT.MODIFY,
                },
                {
                    type: 'save',
                    onClick: () => onSave(file),
                    permission: PERMISSIONS.EVENT.MODIFY,
                },
            ]}
        />
    );
}
